import React, { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../LangContext";
import { SERVICE_OPTIONS } from "../i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";

const inputCls =
  "w-full bg-white border border-[#1e3a5f]/20 rounded-xl px-4 py-3 text-sm text-[#1e3a5f] placeholder:text-[#1e3a5f]/40 focus:outline-none focus:ring-2 focus:ring-[#7a8b50]/50 focus:border-[#7a8b50]/50 transition";
const labelCls = "text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mb-2 block";

export default function QuoteSection() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error(t.common.requiredField);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      toast.success(t.quote.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err, t.quote.error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="presupuesto"
      className="py-24 md:py-32 bg-[#1e3a5f] relative overflow-hidden"
      data-testid="quote-section"
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#7a8b50]/20" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#7a8b50]/10" />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#7a8b50]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#7a8b50] font-semibold">
                Presupuesto
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#FAFAF5] font-medium tracking-tight leading-tight">
              {t.quote.title}
            </h2>
            <p className="mt-5 text-base text-[#FAFAF5]/75 leading-relaxed max-w-md">
              {t.quote.subtitle}
            </p>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="bg-white rounded-3xl p-7 md:p-10 shadow-2xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t.quote.name}</label>
                  <input
                    data-testid="quote-name"
                    className={inputCls}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.quote.phone}</label>
                  <input
                    data-testid="quote-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>{t.quote.email}</label>
                  <input
                    data-testid="quote-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>{t.quote.service}</label>
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger
                      data-testid="quote-service"
                      className="w-full bg-white border-[#1e3a5f]/20 rounded-xl px-4 py-6 text-sm text-[#1e3a5f]"
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`quote-service-opt-${s}`}>
                          {t.serviceKeys[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>{t.quote.message}</label>
                  <textarea
                    data-testid="quote-message"
                    rows={4}
                    className={inputCls}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                  />
                </div>
              </div>

              <button
                data-testid="quote-submit"
                type="submit"
                disabled={submitting}
                className="mt-7 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#7a8b50] text-[#FAFAF5] font-semibold text-sm tracking-wide hover:bg-[#5f6e3d] transition-all w-full md:w-auto disabled:opacity-60"
              >
                {submitting ? t.quote.sending : t.quote.submit}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
